// =============================================================================
// Invoice Types for Invoices Page
// Data source: PostgreSQL via GET /v4/invoices API (V4)
// Legacy data source: MongoDB via GET /v4/legacy/invoices API (V3)
// =============================================================================

import { taxForCategory, type TaxCategory } from '@atek/ca-tax';

export { TAX_CATEGORY_OPTIONS } from '@/shared/lib/tax-category-options';

/**
 * Invoice status values
 */
export type InvoiceStatus = 'preparation' | 'sent' | 'paid' | 'cancelled' | 'expired';

/**
 * Payment terms options
 */
export type PaymentTerms = 'on-order' | 'net-0' | 'net-30' | 'net-45' | 'net-60';

/**
 * Tax category options — sourced from @atek/ca-tax (the full Canadian set incl. HST).
 */
export type { TaxCategory };

/**
 * Currency options
 */
export type Currency = 'CAD' | 'USD' | 'EUR';

/**
 * Language options
 */
export type Language = 'en' | 'fr';

/**
 * Populated organisation (customer)
 */
export interface PopulatedOrganisation {
  _id: string;
  name: string;
  org_num?: number;
}

/**
 * Populated subscription (formerly contract)
 */
export interface PopulatedSubscription {
  _id: string;
  mongoContractId?: string;
  name?: string;
  contract_number?: string;
}

/** @deprecated Use PopulatedSubscription instead */
export type PopulatedContract = PopulatedSubscription;

/**
 * Populated user (sales manager, contractual manager)
 */
export interface PopulatedUser {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

/**
 * Populated site (billing/shipping)
 */
export interface PopulatedSite {
  _id: string;
  name?: string;
  email?: string;
  address?: string;
  city?: string;
  country?: string;
  // NOTE: no province/postal code here on purpose. The invoice populate selects
  // `name email` only, and the underlying Mongo site document spells those two
  // fields `state_province_county` / `postcode` — never `province` /
  // `postal_code`. Read the address from PostgreSQL (GET /v4/sites/:id) instead.
}

/**
 * Populated file (PO file)
 */
export interface PopulatedFile {
  _id: string;
  name?: string;
  url?: string;
}

/**
 * SKU line item in an invoice
 */
export interface InvoiceSku {
  ref_id?: string;
  sku: string;
  description?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  item_number?: number;
  subtotal_id?: string;
  total: number;
}

export interface InvoiceSubtotal {
  _id?: string;
  displayName?: string;
  total: number;
}

/**
 * Invoice note/comment
 */
export interface InvoiceNote {
  date: string;
  content: string;
  user?: string | PopulatedUser;
  user_name?: string;
}

/**
 * Shipping address entry
 */
export interface ShippingAddress {
  site?: string | PopulatedSite;
  address?: string;
}

/**
 * Invoice data from the V3 API
 */
export interface Invoice {
  _id: string;
  /** PostgreSQL UUID (always present for V4-sourced invoices) */
  pgId?: string;
  invoice_number: string;
  invoice_version?: number;
  status: InvoiceStatus;

  // References (may be populated)
  organisation?: string | PopulatedOrganisation;
  customer?: string | PopulatedOrganisation;
  contract?: string | PopulatedContract;
  sales_manager?: string | PopulatedUser;
  contractual_manager?: string | PopulatedUser;

  // Project info
  project_name?: string;
  project_number?: string;
  quote_number?: string;
  po_number?: string;
  po_file?: string | PopulatedFile;

  // Dates
  date?: string;
  expiration_date?: string;
  payment_date?: string;
  created_at?: string;
  updated_at?: string;

  // Payment
  payment_terms?: PaymentTerms;
  payment_ref?: string;

  // Pricing
  currency?: Currency;
  tax_category?: TaxCategory;
  pricelist?: string;
  skus?: InvoiceSku[];
  subtotals?: InvoiceSubtotal[];
  /** Pre-tax subtotal (from V4 subtotal field) */
  subtotal_amount?: number;
  /** Tax amount (from V4 taxAmount field) */
  tax_amount?: number;
  /** Total including tax (from V4 total field — already post-tax) */
  total?: number;

  // Addresses
  billing_site?: string | PopulatedSite;
  billing_address?: string;
  shipping_addresses?: ShippingAddress[];

  // Metadata
  language?: Language;
  tracking_numbers?: string[];
  description?: string;
  comment?: string;
  internal_notes?: string;
  notes?: InvoiceNote[];

  // Next steps
  next_step?: string;
  next_step_deadline?: string;

  // External links
  hubspot_link?: string;
  gdrive_link?: string;
  quickbook_link?: string;

  // System
  deleted?: boolean;

  // DMS document links
  document_count?: number;
}

/**
 * Invoice status statistics
 */
export interface InvoiceStatusStats {
  status: InvoiceStatus | 'total';
  quantity: number;
  amount: number;
}

/**
 * Filters for querying invoices
 */
export interface InvoicesFilters {
  search?: string;
  customer?: string;
  status?: InvoiceStatus | InvoiceStatus[];
  sales_manager?: string;
  contractual_manager?: string;
  account_id_in?: string[];
  sales_manager_id_in?: string[];
  date_from?: string;
  date_to?: string;
  expiration_date_from?: string;
  expiration_date_to?: string;
  payment_date_from?: string;
  payment_date_to?: string;
  // Column-level filters
  invoice_number_search?: string;
  account_name_search?: string;
  created_at_from?: string;
  created_at_to?: string;
  po_number?: string;
  project_name?: string;
  next_step?: string;
  next_step_deadline_from?: string;
  next_step_deadline_to?: string;
  currency?: string;
  contract_search?: string;
  total_min?: string;
  total_max?: string;
  payment_terms_in?: string[];
  tax_category_in?: string[];
  sales_manager_search?: string;
  contractual_manager_search?: string;
  sortKey?: 'invoice_number' | 'created_at' | 'date' | 'expiration_date' | 'payment_date' | 'total' | 'customer';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v4/legacy/invoices
 */
export interface V3InvoicesResponse {
  invoices: Invoice[];
  stats?: InvoiceStatusStats[];
  pageNumber: number;
  pageLength: number;
  sortKey?: string;
  sortDirection?: number;
  total?: number;
}

/**
 * Paginated response wrapper
 */
export interface InvoicesResponse {
  invoices: Invoice[];
  stats: InvoiceStatusStats[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Input for creating a new invoice
 */
export interface CreateInvoiceInput {
  customer: string;
  contract?: string;
  sales_manager: string;
  contractual_manager?: string;
  project_name: string;
  project_number?: string;
  quote_number?: string;
  po_number?: string;
  date?: string;
  payment_terms?: PaymentTerms;
  currency?: Currency;
  tax_category?: TaxCategory;
  language?: Language;
  skus?: InvoiceSku[];
  billing_site?: string;
  billing_address?: string;
  description?: string;
  hubspot_link?: string;
  gdrive_link?: string;
}

/**
 * Input for updating an invoice
 */
export interface UpdateInvoiceInput {
  invoice_number?: string;
  /** Document version, stored as a string in Mongo (e.g. "1", "2"). */
  invoice_version?: string;
  status?: InvoiceStatus;
  customer?: string;
  contract?: string | null;
  sales_manager?: string;
  contractual_manager?: string | null;
  project_name?: string;
  project_number?: string;
  quote_number?: string;
  po_number?: string;
  /** File id from the legacy files store; null detaches the current PO file. */
  po_file?: string | null;
  date?: string;
  expiration_date?: string;
  payment_date?: string;
  payment_terms?: PaymentTerms;
  payment_ref?: string;
  currency?: Currency;
  tax_category?: TaxCategory;
  pricelist?: string;
  language?: Language;
  skus?: InvoiceSku[];
  subtotals?: InvoiceSubtotal[];
  total?: number;
  billing_site?: string;
  billing_address?: string;
  shipping_addresses?: ShippingAddress[];
  tracking_numbers?: string[];
  description?: string;
  comment?: string;
  internal_notes?: string;
  next_step?: string;
  next_step_deadline?: string;
  hubspot_link?: string;
  gdrive_link?: string;
}

/**
 * Input for adding a note to an invoice
 */
export interface AddInvoiceNoteInput {
  content: string;
  date?: string;
  // No `user`: mongo_server types notes[].user as an ObjectId. The V4 session
  // only exposes a PG UUID (useSession()._id), which is uncastable to ObjectId
  // and 500s the note write. Attribution is carried by user_name only.
  user_name?: string;
}

/**
 * Organisation for dropdown filter (customer)
 */
export interface Organisation {
  _id: string;
  name: string;
  mongoOrgId?: string;
  accountNumber?: string;
  org_num?: number;
  enabled?: boolean;
  /** PG account lifecycle status: active, churned, suspended, prospect, onboarding. */
  status?: string;
}

/**
 * User for dropdown filter (managers)
 */
export interface User {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

// =============================================================================
// Type Guards
// =============================================================================

export function isPopulatedOrganisation(
  org: string | PopulatedOrganisation | undefined
): org is PopulatedOrganisation {
  return typeof org === 'object' && org !== null && '_id' in org;
}

export function isPopulatedSubscription(
  contract: string | PopulatedSubscription | undefined
): contract is PopulatedSubscription {
  return typeof contract === 'object' && contract !== null && '_id' in contract;
}

/** @deprecated Use isPopulatedSubscription instead */
export function isPopulatedContract(
  contract: string | PopulatedContract | undefined
): contract is PopulatedContract {
  return isPopulatedSubscription(contract);
}

export function isPopulatedUser(user: string | PopulatedUser | undefined): user is PopulatedUser {
  return typeof user === 'object' && user !== null && '_id' in user;
}

export function isPopulatedSite(site: string | PopulatedSite | undefined): site is PopulatedSite {
  return typeof site === 'object' && site !== null && '_id' in site;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the display name for a customer
 */
export function getCustomerName(customer: string | PopulatedOrganisation | undefined): string {
  if (!customer) return '-';
  if (isPopulatedOrganisation(customer)) {
    return customer.name || '-';
  }
  return customer;
}

/**
 * Get the display name for a subscription (formerly contract)
 */
export function getSubscriptionDisplay(contract: string | PopulatedSubscription | undefined): string {
  if (!contract) return '-';
  if (isPopulatedSubscription(contract)) {
    const parts = [];
    if (contract.contract_number) parts.push(contract.contract_number);
    if (contract.name) parts.push(contract.name);
    return parts.join(' - ') || '-';
  }
  return contract;
}

/** @deprecated Use getSubscriptionDisplay instead */
export function getContractDisplay(contract: string | PopulatedContract | undefined): string {
  return getSubscriptionDisplay(contract);
}

/**
 * Get the display name for a user
 */
export function getUserName(user: string | PopulatedUser | undefined): string {
  if (!user) return '-';
  if (isPopulatedUser(user)) {
    const parts = [];
    if (user.firstname) parts.push(user.firstname);
    if (user.lastname) parts.push(user.lastname);
    return parts.join(' ') || user.email || '-';
  }
  return user;
}

/**
 * Calculate tax amount based on category
 */
export function calculateTax(amount: number, taxCategory: TaxCategory | undefined): number {
  return taxForCategory(amount, taxCategory);
}

/**
 * Calculate total with tax
 */
export function calculateTotalWithTax(
  amount: number,
  taxCategory: TaxCategory | undefined
): number {
  return amount + calculateTax(amount, taxCategory);
}

// =============================================================================
// V4 Types (PostgreSQL-backed with camelCase field names)
// =============================================================================

/**
 * Invoice data from the V4 API (PostgreSQL)
 */
export interface V4Invoice {
  id: string;
  invoiceNumber: string;
  invoiceVersion?: string;
  status: InvoiceStatus;

  // Dates
  invoiceDate: string | null;
  dueDate: string | null;
  expirationDate: string | null;
  paymentDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;

  // Financial
  subtotal: string;
  taxAmount: string;
  total: string;
  currency: Currency;
  taxCategory: TaxCategory | null;

  // Project info
  projectName: string | null;
  projectNumber: string | null;
  poNumber: string | null;

  // Payment
  paymentTerms: PaymentTerms | null;
  paymentRef: string | null;

  // Managers (MongoDB ObjectIds)
  salesManagerId: string | null;
  contractualManagerId: string | null;
  // Enriched manager info
  salesManagerName: string | null;
  contractualManagerName: string | null;
  contractualManagerEmail: string | null;
  contractualManagerPhone: string | null;

  // External links
  hubspotLink: string | null;
  quickbooksInvoiceId: string | null;
  quickbooksLink: string | null;
  gdriveLink: string | null;

  // Workflow
  nextStep: string | null;
  nextStepDeadline: string | null;

  // AR Definition-of-Done — see apps/mastra/src/mastra/tools/acc-validate-invoice.ts
  // ar_done_at = first time all DONE_CHECKS passed; null = not yet done.
  arDoneAt: string | null;
  arDoneChecksAt: string | null;

  // Account info (joined)
  accountId: string;
  accountName: string | null;
  accountNumber: string | null;

  // Company-level accounting metadata (joined from public.companies)
  // Populated by V4 invoice GET-by-id when the invoice's account is linked to a CRM company.
  apContactEmails?: string[] | null;
  poEmailRouting?: string | null;

  // MongoDB references for backward compatibility
  mongoInvoiceId: string | null;

  // Contract and quote references
  contractId: string | null;
  subscriptionId?: string | null;
  quoteId: string | null;
  // Contract info (joined)
  contractNumber: string | null;
  contractName: string | null;
  subscriptionNumber?: string | null;
  subscriptionName?: string | null;

  createdBy: string | null;

  // DMS document links
  documentCount: number;
}

/**
 * Invoice document link (junction table row)
 */
export interface InvoiceDocument {
  id: string;
  documentId: string;
  addedBy: string | null;
  createdAt: string;
}

/**
 * Filters for V4 invoices query
 */
export interface V4InvoicesFilters {
  search?: string;
  accountId?: string;
  status?: InvoiceStatus;
  status_in?: InvoiceStatus[];
  salesManagerId?: string;
  contractualManagerId?: string;
  dateFrom?: string;
  dateTo?: string;
  expirationDateFrom?: string;
  expirationDateTo?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  sortKey?:
    | 'invoiceNumber'
    | 'createdAt'
    | 'invoiceDate'
    | 'dueDate'
    | 'paymentDate'
    | 'total'
    | 'accountName'
    | 'status';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v4/invoices
 */
export interface V4InvoicesResponse {
  invoices: V4Invoice[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * V4 invoice status stats (from PostgreSQL)
 */
export interface V4InvoiceStatusStats {
  status: InvoiceStatus | 'total';
  count: number;
  amount: number;
}

/**
 * API response for GET /v4/invoices/stats
 */
export interface V4InvoiceStatsResponse {
  stats: V4InvoiceStatusStats[];
}

/**
 * Transform V4 invoice to V3-compatible format for backward compatibility
 */
export function transformV4InvoiceToV3(v4Invoice: V4Invoice): Invoice {
  const contractId = v4Invoice.contractId || v4Invoice.subscriptionId || null;
  const contractNumber = v4Invoice.contractNumber || v4Invoice.subscriptionNumber || null;
  const contractName = v4Invoice.contractName || v4Invoice.subscriptionName || null;

  return {
    _id: v4Invoice.mongoInvoiceId || v4Invoice.id,
    pgId: v4Invoice.id,
    invoice_number: v4Invoice.invoiceNumber,
    invoice_version: v4Invoice.invoiceVersion ? parseInt(v4Invoice.invoiceVersion, 10) : undefined,
    status: v4Invoice.status,
    // Customer is now account info
    customer: v4Invoice.accountName
      ? {
          _id: v4Invoice.accountId,
          name: v4Invoice.accountName,
        }
      : v4Invoice.accountId,
    // Contract info (joined from PG)
    contract: contractNumber
      ? {
          _id: contractId || '',
          contract_number: contractNumber,
          name: contractName || undefined,
        }
      : contractId || undefined,
    // Managers with enriched info
    sales_manager: v4Invoice.salesManagerName
      ? {
          _id: v4Invoice.salesManagerId || '',
          firstname: v4Invoice.salesManagerName.split(' ')[0],
          lastname: v4Invoice.salesManagerName.split(' ').slice(1).join(' '),
        }
      : v4Invoice.salesManagerId || undefined,
    contractual_manager: v4Invoice.contractualManagerName
      ? {
          _id: v4Invoice.contractualManagerId || '',
          firstname: v4Invoice.contractualManagerName.split(' ')[0],
          lastname: v4Invoice.contractualManagerName.split(' ').slice(1).join(' '),
          email: v4Invoice.contractualManagerEmail || undefined,
        }
      : v4Invoice.contractualManagerId || undefined,
    // Project info
    project_name: v4Invoice.projectName || undefined,
    project_number: v4Invoice.projectNumber || undefined,
    po_number: v4Invoice.poNumber || undefined,
    // Dates
    date: v4Invoice.invoiceDate || undefined,
    expiration_date: v4Invoice.expirationDate || v4Invoice.dueDate || undefined,
    payment_date: v4Invoice.paymentDate || undefined,
    created_at: v4Invoice.createdAt || undefined,
    updated_at: v4Invoice.updatedAt || undefined,
    // Payment
    payment_terms: v4Invoice.paymentTerms || undefined,
    payment_ref: v4Invoice.paymentRef || undefined,
    // Financial — V4 total is already post-tax (subtotal + taxAmount = total)
    currency: v4Invoice.currency,
    tax_category: v4Invoice.taxCategory || undefined,
    subtotal_amount: parseFloat(v4Invoice.subtotal),
    tax_amount: parseFloat(v4Invoice.taxAmount),
    total: parseFloat(v4Invoice.total),
    // External links
    hubspot_link: v4Invoice.hubspotLink || undefined,
    gdrive_link: v4Invoice.gdriveLink || undefined,
    quickbook_link: v4Invoice.quickbooksLink || undefined,
    // Workflow
    next_step: v4Invoice.nextStep || undefined,
    next_step_deadline: v4Invoice.nextStepDeadline || undefined,
    // DMS document count
    document_count: v4Invoice.documentCount ?? 0,
  };
}
